import type { Address } from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';

import { formatDate, parseMoney } from './format';

/**
 * Regras puras da consulta de CNPJ que preenche o cadastro (ADR 0092).
 *
 * A consulta e a dos dados publicos da Receita Federal pela BrasilAPI. Ela so preenche
 * campo vazio: o que a pessoa ja digitou fica como esta, e a tela pergunta antes de
 * trocar. E o comportamento mais seguro: uma consulta que volta depois de a pessoa
 * digitar nao apaga o que ela escreveu, e o dado da Receita nunca entra sem que a
 * pessoa veja.
 */

/** Os catorze digitos do CNPJ com os digitos verificadores certos, ou null. */
export function cnpjCompleto(valor: string): string | null {
  const digitos = valor.replace(/\D/g, '');
  if (digitos.length !== 14 || /^(\d)\1{13}$/.test(digitos)) return null;
  const digito = (base: string, pesos: number[]) => {
    const soma = pesos.reduce((total, peso, i) => total + Number(base[i]) * peso, 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const primeiro = digito(digitos.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const segundo = digito(digitos.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  if (Number(digitos[12]) !== primeiro || Number(digitos[13]) !== segundo) return null;
  return digitos;
}

/**
 * Dinheiro no formato brasileiro do campo editavel: "1.000.000,00".
 *
 * Sempre com a virgula e os centavos, porque `parseMoney` le o ponto sem virgula como
 * decimal. A conta e feita sobre os digitos do inteiro, sem ponto flutuante.
 */
export function dinheiroParaCampo(valor: Money | null | undefined): string {
  if (!valor || valor.amount <= 0 || !Number.isSafeInteger(valor.amount)) return '';
  const exponent = valor.exponent;
  const digitos = String(valor.amount).padStart(exponent + 1, '0');
  const inteiro = exponent > 0 ? digitos.slice(0, -exponent) : digitos;
  const fracao = exponent > 0 ? digitos.slice(-exponent) : '';
  const comMilhar = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return fracao ? `${comMilhar},${fracao}` : comMilhar;
}

/** O aviso da origem dos dados: "Dados da Receita Federal pela BrasilAPI em 27/10/2026". */
export function avisoDaReceita(consultadoEm: string): string {
  return `Dados da Receita Federal pela SeguraCoreData em ${formatDate(consultadoEm)}`;
}

/** Um campo do formulario com o que a Receita devolveu para ele. */
export interface CampoDaReceita<C extends string = string> {
  chave: C;
  rotulo: string;
  /** O que o formulario tem agora, para mostrar. Vazio e campo vazio. */
  atual: string;
  /** O que a Receita trouxe, para mostrar. */
  receita: string;
  /** Como comparar os dois. Sem ele, texto sem caixa, sem acento e sem espaco a mais. */
  igual?: (atual: string, receita: string) => boolean;
}

/** Texto sem caixa, sem acento e sem espaco a mais, para comparar. */
export function textoComparavel(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Iguais pelos digitos (telefone, CEP, CNAE). */
export function digitosIguais(atual: string, receita: string): boolean {
  return atual.replace(/\D/g, '') === receita.replace(/\D/g, '');
}

/** Iguais pelo valor em centavos. */
export function dinheiroIgual(atual: string, receita: string): boolean {
  const a = parseMoney(atual);
  const b = parseMoney(receita);
  return a !== null && b !== null && a.amount === b.amount;
}

/**
 * Separa o que preencher do que perguntar.
 *
 * Preenche o campo vazio que a Receita trouxe. O campo com valor diferente vai para a
 * pergunta; o igual e o que a Receita nao trouxe ficam como estao.
 */
export function planoDoPreenchimento<C extends string>(
  campos: CampoDaReceita<C>[],
): { preencher: CampoDaReceita<C>[]; divergentes: CampoDaReceita<C>[] } {
  const preencher: CampoDaReceita<C>[] = [];
  const divergentes: CampoDaReceita<C>[] = [];
  for (const campo of campos) {
    if (campo.receita.trim() === '') continue;
    if (campo.atual.trim() === '') {
      preencher.push(campo);
      continue;
    }
    const igual = campo.igual
      ? campo.igual(campo.atual, campo.receita)
      : textoComparavel(campo.atual) === textoComparavel(campo.receita);
    if (!igual) divergentes.push(campo);
  }
  return { preencher, divergentes };
}

/** O endereco numa linha, para mostrar na pergunta. */
export function enderecoEmTexto(endereco: Partial<Address>): string {
  const cep = (endereco.zip_code ?? '').replace(/\D/g, '');
  const partes = [
    endereco.street,
    endereco.number,
    endereco.complement,
    endereco.district,
    [endereco.city, endereco.state].filter(Boolean).join('/'),
    cep.length === 8 ? `CEP ${cep.slice(0, 5)}-${cep.slice(5)}` : cep,
  ];
  return partes.filter((parte) => parte && parte.trim() !== '').join(', ');
}

/** Sem CEP, logradouro, numero, bairro, cidade nem UF: o endereco nao foi digitado. */
export function enderecoVazio(endereco: Address): boolean {
  return [
    endereco.zip_code,
    endereco.street,
    endereco.number,
    endereco.district,
    endereco.city,
    endereco.state,
  ].every((valor) => !valor || valor.trim() === '');
}

/** O mesmo endereco, pelo CEP e pelo texto sem caixa nem acento. */
export function mesmoEndereco(a: Partial<Address>, b: Partial<Address>): boolean {
  return (
    digitosIguais(a.zip_code ?? '', b.zip_code ?? '') &&
    (['street', 'number', 'complement', 'district', 'city', 'state'] as const).every(
      (campo) => textoComparavel(a[campo] ?? '') === textoComparavel(b[campo] ?? ''),
    )
  );
}
